// src/services/comisionesRecobroService.js
//
// Cuando se cancela una deuda donde ya se pagó parte (o toda) la comisión
// al comisionista, queda un saldo a recobrar (o a perdonar). Esta capa
// maneja:
//   - listar las comisiones con recobroPendiente.estado === "pendiente"
//   - marcar perdonado (decisión administrativa — el comisionista no devuelve)
//   - marcar recuperado parcial/total (registra movimiento de caja entrada)
//
// El doc comision puede tener:
//   recobroPendiente: {
//     monto: number,                  // total que se pagó originalmente
//     estado: "pendiente" | "perdonado" | "recuperado",
//     motivoCancelacion: string,
//     deudaIdOrigen: string,
//     fechaCancelacion: Timestamp,
//     montoRecuperado: number,        // acumulado de pagos del comisionista
//     fechaCierre: Timestamp,         // se setea al perdonar o recuperar 100%
//     motivoPerdon: string,           // si fue perdonado
//   }

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  Timestamp,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../Firebase/firebaseconfig";
import { registrarAuditoria } from "../helpers/auditoria";
import { metodoPagoPayload } from "../helpers/metodosPago";
import { hoyLocalISO } from "../helpers/fechas";

// ── Listar comisiones con recobro pendiente ─────────────────────────────────

/**
 * Lista comisiones canceladas con recobro pendiente.
 *
 * @param {object} [opts]
 * @param {string} [opts.estado="pendiente"]  pendiente | perdonado | recuperado
 * @returns {Promise<Array>}
 */
export async function listarComisionesConRecobro({ estado = "pendiente" } = {}) {
  // Query por campo anidado. Firestore lo soporta con dot-notation.
  try {
    const q = query(
      collection(db, "comisiones"),
      where("recobroPendiente.estado", "==", estado)
    );
    const snap = await getDocs(q);
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => {
      const ta = a.recobroPendiente?.fechaCancelacion?.toMillis?.() ?? 0;
      const tb = b.recobroPendiente?.fechaCancelacion?.toMillis?.() ?? 0;
      return tb - ta;
    });
    return items;
  } catch (err) {
    console.warn("listarComisionesConRecobro:", err);
    return [];
  }
}

// ── Marcar perdonado ────────────────────────────────────────────────────────

/**
 * Decisión administrativa: la empresa absorbe la comisión pagada y no
 * exige reembolso al comisionista. Registra el cierre en auditoría.
 */
export async function perdonarRecobro(comisionId, motivo = "") {
  if (!comisionId) throw new Error("comisionId es obligatorio.");

  const cRef = doc(db, "comisiones", comisionId);
  const snap = await getDoc(cRef);
  if (!snap.exists()) throw new Error("Comisión no encontrada.");
  const c = snap.data();
  if (!c.recobroPendiente || c.recobroPendiente.estado !== "pendiente") {
    throw new Error("La comisión no tiene un recobro pendiente.");
  }

  await updateDoc(cRef, {
    recobroPendiente: {
      ...c.recobroPendiente,
      estado: "perdonado",
      motivoPerdon: motivo || "",
      fechaCierre: Timestamp.now(),
    },
    updatedAt: serverTimestamp(),
  });

  registrarAuditoria({
    accion: "comisiones.recobroPerdonado",
    entidadTipo: "comision",
    entidadId: comisionId,
    detalle: `Recobro perdonado: ${motivo || "(sin motivo)"} — monto $${c.recobroPendiente.monto}`,
    despues: {
      monto: c.recobroPendiente.monto,
      motivoPerdon: motivo || "",
    },
  }).catch((e) => console.warn("Audit perdonarRecobro:", e));
}

// ── Marcar recuperado (parcial o total) ─────────────────────────────────────

/**
 * Registra que el comisionista devolvió X monto. Crea movimiento de caja
 * tipo "entrada" para el proyecto/caja original. Si el acumulado iguala
 * el monto pendiente, marca estado "recuperado".
 *
 * @param {string} comisionId
 * @param {object} opts
 * @param {number} opts.monto       Monto recuperado en esta operación
 * @param {string} opts.metodo      "efectivo" | "transferencia" | ...
 * @param {string} [opts.referencia]
 * @param {string} [opts.notas]
 */
export async function registrarRecobro(comisionId, { monto, metodo = "efectivo", referencia = "", notas = "" }) {
  if (!comisionId) throw new Error("comisionId es obligatorio.");
  const montoNum = Number(monto);
  if (!Number.isFinite(montoNum) || montoNum <= 0) {
    throw new Error("Monto inválido.");
  }

  const auth = getAuth();
  const user = auth.currentUser;
  const uid = user?.uid || null;

  let cajaMovimientoId = null;

  await runTransaction(db, async (tx) => {
    const cRef = doc(db, "comisiones", comisionId);
    const cSnap = await tx.get(cRef);
    if (!cSnap.exists()) throw new Error("Comisión no encontrada.");
    const c = cSnap.data();
    if (!c.recobroPendiente || c.recobroPendiente.estado === "perdonado") {
      throw new Error("La comisión no admite recobro (perdonado o sin recobro pendiente).");
    }
    const pendienteMonto = Number(c.recobroPendiente.monto || 0);
    const yaRecuperado = Number(c.recobroPendiente.montoRecuperado || 0);
    const restante = Math.max(pendienteMonto - yaRecuperado, 0);
    if (montoNum > restante + 0.5) {
      throw new Error(`El monto excede lo pendiente por recobrar ($${restante.toFixed(2)}).`);
    }

    // Movimiento de caja (entrada) — usar el contexto del proyecto original
    // si la comisión lo trae (proyectoId, fraccionamientoId, propiedadId).
    if (c.proyectoId) {
      const cajaRef = doc(collection(db, "cajas_movimientos"));
      cajaMovimientoId = cajaRef.id;
      const metodoInfo = metodoPagoPayload(metodo);
      tx.set(cajaRef, {
        tipo: "entrada",
        monto: montoNum,
        concepto: `Recobro de comisión cancelada — ${c.comisionistaNombre || ""}`,
        conceptoTipo: "Recobro de comisión",
        categoria: "recobro_comision",
        fecha: hoyLocalISO(),
        fechaMs: Date.now(),
        cajaScope: "proyecto",
        cajaId: c.proyectoId,
        clusterId: "",
        proyectoId: c.proyectoId,
        proyectoNombre: c.proyectoNombre || "",
        fraccionamientoId: c.fraccionamientoId || "",
        fraccionamientoNombre: c.fraccionamientoNombre || "",
        propiedadId: c.propiedadId || "",
        propiedadFolio: c.propiedadFolio || "",
        cxpId: "",
        deudaId: c.recobroPendiente.deudaIdOrigen || "",
        comisionId,
        origenTipo: "recobro_comision",
        origen: "comisiones.recobro",
        ...metodoInfo,
        referencia,
        notas,
        activo: true,
        cancelado: false,
        createdAt: serverTimestamp(),
        createdAtMs: Date.now(),
        createdByUid: uid || "",
        createdByEmail: user?.email || "",
      });
    }

    const nuevoAcumulado = Math.round((yaRecuperado + montoNum) * 100) / 100;
    const liquidado = nuevoAcumulado >= pendienteMonto - 0.5;

    tx.update(cRef, {
      recobroPendiente: {
        ...c.recobroPendiente,
        montoRecuperado: nuevoAcumulado,
        estado: liquidado ? "recuperado" : "pendiente",
        ...(liquidado ? { fechaCierre: Timestamp.now() } : {}),
        ultimoCajaMovimientoId: cajaMovimientoId,
      },
      updatedAt: serverTimestamp(),
    });
  });

  registrarAuditoria({
    accion: "comisiones.recobroRegistrado",
    entidadTipo: "comision",
    entidadId: comisionId,
    detalle: `Recobro de $${montoNum} registrado (método: ${metodo})`,
    despues: { monto: montoNum, metodo, cajaMovimientoId, referencia, notas },
  }).catch((e) => console.warn("Audit registrarRecobro:", e));

  return { cajaMovimientoId };
}
